// Payroll export for the "Отработка" tab. Builds a semicolon-delimited CSV
// (what RU Excel expects) with a UTF-8 BOM so Cyrillic opens cleanly, or a
// PNG table for messaging to an accountant. Either downloads (web) or copies
// to the clipboard (native). Dependency-free.

import { Platform } from 'react-native';

// ₽ per shift when an employee has no personal rate set (Сотрудники → карточка).
export const DEFAULT_DAY_RATE = 3500;

const q = (s) => {
  const v = String(s == null ? '' : s);
  return /[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
};
// RU decimal — comma, two places. Payroll wants a number, not "5 ч 30 мин".
const dec = (h) => (Math.round(h * 100) / 100).toFixed(2).replace('.', ',');
const rub = (n) => `${Math.round(n).toLocaleString('ru-RU')} ₽`;
const kv = (obj, fmt) => Object.entries(obj)
  .sort((a, b) => b[1] - a[1])
  .map(([k, v]) => `${k}: ${fmt(v)}`)
  .join('; ');

export function resolveRate(payRate) {
  if (payRate && (payRate.kind === 'perHour' || payRate.kind === 'perDay') && Number(payRate.amount) > 0) {
    return { kind: payRate.kind, amount: Number(payRate.amount), isDefault: false };
  }
  return { kind: 'perDay', amount: DEFAULT_DAY_RATE, isDefault: true };
}

// row needs { total (hours), days (unique shift-days), shifts }.
export function computePay(row, payRate) {
  const rate = resolveRate(payRate);
  const units = rate.kind === 'perHour' ? row.total : (row.days || row.shifts || 0);
  const amount = Math.round(rate.amount * units);
  const label = rate.kind === 'perHour' ? `${rate.amount} ₽/ч` : `${rate.amount} ₽/смена`;
  return { amount, label, isDefault: rate.isDefault, kind: rate.kind };
}

export function buildHoursCsv(rows, { nameById = {}, locById = {}, periodLabel = '', payRateById = {} } = {}) {
  const now = new Date();
  const stamp = `${String(now.getDate()).padStart(2, '0')}.${String(now.getMonth() + 1).padStart(2, '0')}.${now.getFullYear()}`;
  const head = ['Сотрудник', 'Смен', 'Часы', 'Часы (десятичн.)', 'Ставка', 'К выплате, ₽', 'По должностям', 'По точкам'];
  let grandPay = 0;
  const body = rows.map((r) => {
    const pay = computePay(r, payRateById[r.employeeId]);
    grandPay += pay.amount;
    return [
      nameById[r.employeeId] || r.employeeId,
      r.days || r.shifts,
      fmtHuman(r.total),
      dec(r.total),
      pay.label + (pay.isDefault ? ' (общая)' : ''),
      Math.round(pay.amount),
      kv(r.byPosition, fmtHuman),
      kv(mapKeys(r.byLocation, (id) => locById[id]?.name || id), fmtHuman),
    ];
  });
  const grand = rows.reduce((s, r) => s + r.total, 0);
  const grandShifts = rows.reduce((s, r) => s + (r.days || r.shifts), 0);
  const lines = [
    [`Отработка «Якобы»${periodLabel ? ` — ${periodLabel}` : ''}`, `выгружено ${stamp}`],
    [],
    head,
    ...body,
    [],
    ['ИТОГО', grandShifts, fmtHuman(grand), dec(grand), '', Math.round(grandPay), '', ''],
  ];
  const csv = lines.map((cols) => cols.map(q).join(';')).join('\r\n');
  return `﻿${csv}`;
}

function fmtHuman(h) {
  const total = Math.round(h * 60);
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return mm ? `${hh} ч ${mm} мин` : `${hh} ч`;
}
function mapKeys(obj, fn) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) out[fn(k)] = v;
  return out;
}

// Returns { ok, mode: 'download' | 'clipboard' | 'none' }.
export async function saveTextFile(filename, text, mime = 'text/csv;charset=utf-8') {
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    try {
      const blob = new Blob([text], { type: mime });
      triggerDownload(blob, filename);
      return { ok: true, mode: 'download' };
    } catch { /* fall through to clipboard */ }
  }
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return { ok: true, mode: 'clipboard' };
    }
  } catch { /* ignore */ }
  return { ok: false, mode: 'none' };
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export function hoursExportFilename(periodKey, ext = 'csv') {
  const d = new Date();
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `yakoby-otrabotka-${periodKey}-${iso}.${ext}`;
}

// Renders the payroll table to a PNG (craft-paper look) and downloads it.
// Web only — returns { ok:false } elsewhere.
export async function saveHoursImage(rows, { nameById = {}, periodLabel = '', payRateById = {}, filename = 'otrabotka.png' } = {}) {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return { ok: false, mode: 'none' };
  try {
    const dpr = Math.min(3, Math.max(2, (typeof window !== 'undefined' && window.devicePixelRatio) || 2));
    const W = 760;
    const padX = 32;
    const rowH = 46;
    const headerH = 132;
    const footH = 64;
    const H = headerH + rows.length * rowH + footH + 28;
    const cv = document.createElement('canvas');
    cv.width = W * dpr;
    cv.height = H * dpr;
    const g = cv.getContext('2d');
    g.scale(dpr, dpr);

    const INK = '#2a2320';
    const MUTED = '#8a7d70';
    const PAPER = '#efe7d5';
    const LINE = '#d9cdb6';
    const RED = '#c0362a';

    g.fillStyle = PAPER;
    g.fillRect(0, 0, W, H);

    g.fillStyle = INK;
    g.font = '700 26px Georgia, "Times New Roman", serif';
    g.fillText('Отработка «Якобы»', padX, 46);
    g.fillStyle = MUTED;
    g.font = '400 14px Georgia, serif';
    const stamp = new Date().toLocaleDateString('ru-RU');
    g.fillText(`${periodLabel ? periodLabel + ' · ' : ''}выгружено ${stamp}`, padX, 68);

    // columns: name | смен | часы | к выплате (right-aligned money)
    const colShift = W - padX - 330;
    const colHours = W - padX - 210;
    const colPay = W - padX;
    g.fillStyle = MUTED;
    g.font = '700 12px Georgia, serif';
    g.fillText('СОТРУДНИК', padX, headerH - 20);
    g.textAlign = 'right';
    g.fillText('СМЕН', colShift, headerH - 20);
    g.fillText('ЧАСЫ', colHours, headerH - 20);
    g.fillText('К ВЫПЛАТЕ', colPay, headerH - 20);
    g.textAlign = 'left';
    g.strokeStyle = RED;
    g.lineWidth = 2;
    g.beginPath();
    g.moveTo(padX, headerH - 8);
    g.lineTo(W - padX, headerH - 8);
    g.stroke();

    let y = headerH + 20;
    let grandPay = 0;
    let grandHours = 0;
    let grandShifts = 0;
    for (const r of rows) {
      const pay = computePay(r, payRateById[r.employeeId]);
      grandPay += pay.amount;
      grandHours += r.total;
      grandShifts += r.days || r.shifts;
      g.fillStyle = INK;
      g.font = '600 15px Georgia, serif';
      g.fillText(clip(g, nameById[r.employeeId] || r.employeeId, colShift - padX - 16), padX, y);
      g.fillStyle = MUTED;
      g.font = '400 12px Georgia, serif';
      g.fillText(pay.label + (pay.isDefault ? ' · общая' : ''), padX, y + 16);
      g.fillStyle = INK;
      g.font = '400 15px Georgia, serif';
      g.textAlign = 'right';
      g.fillText(String(r.days || r.shifts), colShift, y);
      g.fillText(fmtHuman(r.total), colHours, y);
      g.font = '700 16px Georgia, serif';
      g.fillText(rub(pay.amount), colPay, y);
      g.textAlign = 'left';
      g.strokeStyle = LINE;
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(padX, y + 26);
      g.lineTo(W - padX, y + 26);
      g.stroke();
      y += rowH;
    }

    y += 10;
    g.fillStyle = INK;
    g.font = '700 15px Georgia, serif';
    g.fillText('ИТОГО', padX, y);
    g.textAlign = 'right';
    g.font = '400 15px Georgia, serif';
    g.fillText(String(grandShifts), colShift, y);
    g.fillText(fmtHuman(grandHours), colHours, y);
    g.font = '800 18px Georgia, serif';
    g.fillStyle = RED;
    g.fillText(rub(grandPay), colPay, y);
    g.textAlign = 'left';

    const blob = await new Promise((res) => cv.toBlob(res, 'image/png'));
    if (!blob) return { ok: false, mode: 'none' };
    triggerDownload(blob, filename);
    return { ok: true, mode: 'download' };
  } catch {
    return { ok: false, mode: 'none' };
  }
}

function clip(g, text, maxW) {
  let t = String(text || '');
  if (g.measureText(t).width <= maxW) return t;
  while (t.length > 1 && g.measureText(`${t}…`).width > maxW) t = t.slice(0, -1);
  return `${t}…`;
}
